// src/pages/qc/PageQCCustomerSamples.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { CustomerSampleModal } from "./CustomerSampleModal";
import { MonthYearSelect } from "../../components/MonthYearSelect";
import { currentMonthKey } from "../../utils/dateUtils";
import { useToast } from "../../components/Toast";

// ── Constants ─────────────────────────────────────────────────────────────────

const SUBMISSION_STATUS = [
  { key: "SUBMITTED", label: "Submitted", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
  { key: "RECEIVED",  label: "Received",  color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  { key: "TESTED",    label: "Tested",    color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
];
const STATUS_MAP_S = Object.fromEntries(SUBMISSION_STATUS.map(s => [s.key, s]));

const OUTCOME_OPTIONS = [
  { key: "",            label: "— outcome —" },
  { key: "GRADE_MATCH", label: "Grade Match" },
  { key: "NEW_PRODUCT", label: "New Product Needed" },
];

// ── Small components ──────────────────────────────────────────────────────────

function SubmissionStatusBadge({ status }) {
  const cfg = STATUS_MAP_S[status] ?? STATUS_MAP_S.SUBMITTED;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>{cfg.label}</span>
  );
}

// ── Matching engine ───────────────────────────────────────────────────────────

const VARIANCE = 0.10;

function withinVariance(value, min, max) {
  if (value == null) return null;
  if (min == null && max == null) return null;
  const lo = min != null ? min * (1 - VARIANCE) : -Infinity;
  const hi = max != null ? max * (1 + VARIANCE) : Infinity;
  return value >= lo && value <= hi;
}

function centreScore(value, min, max) {
  if (value == null || (min == null && max == null)) return 0;
  const mid  = ((min ?? max) + (max ?? min)) / 2;
  const half = Math.abs((max ?? mid) - (min ?? mid)) / 2 || 1;
  return Math.abs(value - mid) / half;
}

function matchSpecs(sample, specs) {
  const results = [];
  for (const spec of specs) {
    const hasAnySpec = spec.solidContentMin != null || spec.solidContentMax != null ||
                       spec.b4CupMin != null || spec.b4CupMax != null ||
                       spec.brookfieldMin != null || spec.brookfieldMax != null;
    if (!hasAnySpec) continue;

    const solidMatch = withinVariance(
      sample.solidPct  != null ? parseFloat(sample.solidPct)  : null,
      spec.solidContentMin != null ? parseFloat(spec.solidContentMin) : null,
      spec.solidContentMax != null ? parseFloat(spec.solidContentMax) : null,
    );

    let viscMatch = null;
    let viscScore = 0;
    const viscVal = sample.viscosity != null ? parseFloat(sample.viscosity) : null;
    if (sample.viscUnit === "B4_CUP" && (spec.b4CupMin != null || spec.b4CupMax != null)) {
      viscMatch = withinVariance(viscVal, parseFloat(spec.b4CupMin), parseFloat(spec.b4CupMax));
      viscScore = centreScore(viscVal, parseFloat(spec.b4CupMin), parseFloat(spec.b4CupMax));
    } else if (sample.viscUnit === "BROOKFIELD" && (spec.brookfieldMin != null || spec.brookfieldMax != null)) {
      viscMatch = withinVariance(viscVal, parseFloat(spec.brookfieldMin), parseFloat(spec.brookfieldMax));
      viscScore = centreScore(viscVal, parseFloat(spec.brookfieldMin), parseFloat(spec.brookfieldMax));
    }

    if (solidMatch === false || viscMatch === false) continue;

    const solidScore = centreScore(
      sample.solidPct != null ? parseFloat(sample.solidPct) : null,
      spec.solidContentMin != null ? parseFloat(spec.solidContentMin) : null,
      spec.solidContentMax != null ? parseFloat(spec.solidContentMax) : null,
    );

    const matchedOn = [];
    if (solidMatch === true) matchedOn.push("Solids");
    if (viscMatch  === true) matchedOn.push("Viscosity");
    results.push({ spec, solidMatch, viscMatch, score: solidScore + viscScore, matchedOn });
  }
  return results.sort((a, b) => a.score - b.score);
}

// ── RecommendationPanel ───────────────────────────────────────────────────────

function MatchBadge({ match }) {
  if (match === true)  return <span style={{ color: "#15803d", fontWeight: 600 }}>✓</span>;
  if (match === false) return <span style={{ color: "#dc2626", fontWeight: 600 }}>✗</span>;
  return <span style={{ color: "#9ca3af" }}>—</span>;
}

function RecommendationPanel({ sample, specs }) {
  const matches = useMemo(() => matchSpecs(sample, specs), [sample, specs]);

  if (!sample.solidPct && !sample.viscosity) {
    return <div style={{ color: "#9ca3af", fontSize: 12 }}>No values to compare.</div>;
  }
  if (matches.length === 0) {
    return (
      <div style={{ background: "#fff7ed", border: "1px solid #fed7aa",
        borderRadius: 6, padding: "10px 14px", fontSize: 13, color: "#c2410c" }}>
        No matching specifications found within ±10% variance.
        Consider creating a new formulation.
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
        {matches.length} matching product{matches.length !== 1 ? "s" : ""} found (±10% variance)
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <th style={{ padding: "6px 10px", textAlign: "left" }}>Grade</th>
            <th style={{ padding: "6px 10px", textAlign: "center" }}>Solids</th>
            <th style={{ padding: "6px 10px", textAlign: "center" }}>Viscosity</th>
            <th style={{ padding: "6px 10px", textAlign: "left" }}>Spec Range (Solids)</th>
            <th style={{ padding: "6px 10px", textAlign: "left" }}>Spec Range (Visc)</th>
            <th style={{ padding: "6px 10px", textAlign: "left" }}>Match</th>
          </tr>
        </thead>
        <tbody>
          {matches.map(({ spec, solidMatch, viscMatch, matchedOn }, i) => {
            const isBest = i === 0;
            const viscRange = spec.b4CupMin != null
              ? `${spec.b4CupMin}–${spec.b4CupMax} s (B4)`
              : spec.brookfieldMin != null
              ? `${spec.brookfieldMin}–${spec.brookfieldMax} cP`
              : "—";
            return (
              <tr key={spec.fgId} style={{ borderBottom: "1px solid #f1f5f9",
                background: isBest ? "#f0fdf4" : "#fff" }}>
                <td style={{ padding: "7px 10px", fontWeight: 600 }}>
                  {spec.fgId}
                  {isBest && (
                    <span style={{ marginLeft: 6, fontSize: 10, background: "#15803d",
                      color: "#fff", borderRadius: 3, padding: "1px 5px" }}>
                      BEST MATCH
                    </span>
                  )}
                </td>
                <td style={{ padding: "7px 10px", textAlign: "center" }}>
                  <MatchBadge match={solidMatch} />
                </td>
                <td style={{ padding: "7px 10px", textAlign: "center" }}>
                  <MatchBadge match={viscMatch} />
                </td>
                <td style={{ padding: "7px 10px", color: "#6b7280" }}>
                  {spec.solidContentMin ?? "—"}–{spec.solidContentMax ?? "—"} %
                </td>
                <td style={{ padding: "7px 10px", color: "#6b7280" }}>{viscRange}</td>
                <td style={{ padding: "7px 10px", color: "#15803d" }}>
                  {matchedOn.join(", ") || "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── AnalysisModal ─────────────────────────────────────────────────────────────

function AnalysisModal({ sub, samples, specs, canEdit, canDelete, onLogSave, onDelete, onClose }) {
  const matching = samples.filter(s => s.sampleRef === sub.sample_ref);
  const [logModal, setLogModal] = useState(false);

  return (
    <div className="modal wide">
      <div className="modal-header">
        <h3 className="modal-title">Analysis — {sub.sample_ref}</h3>
        <button className="btn" onClick={onClose}>✕</button>
      </div>

      {canEdit && (
        <div style={{ marginBottom: 12 }}>
          <button className="btn btn-primary" style={{ fontSize: 12 }}
            onClick={() => setLogModal(true)}>
            + Log Analysis
          </button>
        </div>
      )}

      {matching.length === 0 ? (
        <div style={{ color: "#9ca3af", fontSize: 13, padding: "16px 0" }}>
          No analysis logged for this sample yet.
        </div>
      ) : (
        matching.map(sample => (
          <div key={sample.id} style={{
            border: "1px solid #e2e8f0", borderRadius: 6, padding: "12px 14px", marginBottom: 12,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                {sample.date}
                {sample.solidPct  != null && ` · Solids: ${sample.solidPct}%`}
                {sample.viscosity != null && ` · Visc: ${sample.viscosity} ${sample.viscUnit === "B4_CUP" ? "s (B4)" : "cP"}`}
                {sample.ph != null && ` · pH: ${sample.ph}`}
              </div>
              {canDelete && (
                <button className="btn-small btn-danger" style={{ fontSize: 11 }}
                  onClick={() => onDelete(sample.id)}>
                  Del
                </button>
              )}
            </div>
            {sample.remarks && (
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                Remarks: {sample.remarks}
              </div>
            )}
            <RecommendationPanel sample={sample} specs={specs} />
          </div>
        ))
      )}

      {logModal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setLogModal(false); }}>
          <CustomerSampleModal
            initialRef={sub.sample_ref}
            onSave={async body => { await onLogSave(body); setLogModal(false); }}
            onCancel={() => setLogModal(false)}
          />
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function PageQCCustomerSamples({ myPerms, fgSpecs }) {
  const showToast = useToast();
  const [samples,   setSamples]   = useState([]);
  const [specs,     setSpecs]     = useState(fgSpecs || []);
  const [subs,      setSubs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [anaModal,  setAnaModal]  = useState(null);
  const [search,    setSearch]    = useState("");
  const [subEdits,  setSubEdits]  = useState({});
  const [subSaving, setSubSaving] = useState(null);

  const [expanded, setExpanded] = useState(null);
  const [monthKey, setMonthKey] = useState(currentMonthKey);

  const canEdit   = myPerms?.includes("QC_EDIT") || myPerms?.includes("RND_EDIT");
  const canDelete = myPerms?.includes("USER_MANAGE");

  useEffect(() => {
    const reqs = [api.fetchCustomerSamples(), api.fetchSampleSubmissions()];
    if (!fgSpecs || fgSpecs.length === 0) reqs.push(api.fetchFgSpecs());
    Promise.all(reqs)
      .then(([s, submissions, sp]) => {
        setSamples(s);
        setSubs(submissions);
        if (sp) setSpecs(sp);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filteredSubs = useMemo(() => {
    const q = search.toLowerCase();
    return subs.filter(s => (s.sample_ref ?? "").toLowerCase().includes(q));
  }, [subs, search]);

  const filteredSamples = useMemo(() => {
    const q = search.toLowerCase();
    return samples.filter(s =>
      (s.date || "").startsWith(monthKey) &&
      (s.sampleRef ?? "").toLowerCase().includes(q)
    );
  }, [samples, search, monthKey]);

  function topMatch(sample) {
    const matches = matchSpecs(sample, specs);
    return matches.length > 0 ? matches[0].spec.fgId : null;
  }

  async function handleCreate(body) {
    const created = await api.createCustomerSample(body);
    setSamples(old => [created, ...old]);
    showToast("Sample logged");
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this analysis record?")) return;
    await api.deleteCustomerSample(id);
    setSamples(old => old.filter(s => s.id !== id));
    showToast("Record deleted", "info");
  }

  async function handleUpdateSubmission(id) {
    const edit = subEdits[id] ?? {};
    setSubSaving(id);
    try {
      const updated = await api.updateSampleSubmission(id, {
        status:         edit.status         ?? null,
        outcome:        edit.outcome        ?? null,
        outcomeNotes:   edit.outcomeNotes   ?? null,
        matchedGrade:   edit.matchedGrade   ?? null,
        newProductName: edit.newProductName ?? null,
      });
      setSubs(old => old.map(s => s.id === id ? updated : s));
      setSubEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
      showToast("Status updated");
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSubSaving(null);
    }
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Customer Samples</div>
          <div className="page-subtitle">Track sample submissions and analysis outcomes</div>
        </div>
      </div>

      <div className="action-bar">
        <input className="form-input" placeholder="Search sample ref…"
          value={search} onChange={e => setSearch(e.target.value)} style={{ width: 260 }} />
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filteredSubs.length === 0 && (
            <div style={{ textAlign: "center", color: "#aaa", padding: 32 }}>No submissions found.</div>
          )}
          {filteredSubs.map(sub => {
            const edit           = subEdits[sub.id] ?? {};
            const currentStatus  = edit.status        ?? sub.status;
            const currentOutcome = edit.outcome        ?? sub.outcome         ?? "";
            const currentNotes   = edit.outcomeNotes   ?? sub.outcome_notes   ?? "";
            const currentGrade   = edit.matchedGrade   ?? sub.matched_grade   ?? "";
            const currentNewProd = edit.newProductName ?? sub.new_product_name ?? "";
            const isDirty        = Object.keys(edit).length > 0;

            return (
              <div key={sub.id} style={{
                border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", padding: "12px 16px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 12, color: "#6b7280", minWidth: 90 }}>{sub.submitted_date}</div>
                  <div style={{ fontWeight: 600, flex: 1, minWidth: 140 }}>{sub.sample_ref}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{sub.submitted_by}</div>

                  <SubmissionStatusBadge status={currentStatus} />

                  {canEdit && (
                    <>
                      <select value={currentStatus}
                        onChange={e => setSubEdits(p => ({ ...p, [sub.id]: { ...edit, status: e.target.value } }))}
                        style={{ fontSize: 12, padding: "4px 8px", borderRadius: 4, border: "1px solid #d1d5db" }}>
                        {SUBMISSION_STATUS.map(s => (
                          <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                      </select>

                      <select value={currentOutcome}
                        onChange={e => setSubEdits(p => ({
                          ...p, [sub.id]: { ...edit, outcome: e.target.value, matchedGrade: "", newProductName: "" },
                        }))}
                        style={{ fontSize: 12, padding: "4px 8px", borderRadius: 4, border: "1px solid #d1d5db" }}>
                        {OUTCOME_OPTIONS.map(o => (
                          <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                      </select>

                      {currentOutcome === "GRADE_MATCH" && (
                        <select value={currentGrade}
                          onChange={e => setSubEdits(p => ({ ...p, [sub.id]: { ...edit, matchedGrade: e.target.value } }))}
                          style={{ fontSize: 12, padding: "4px 8px", borderRadius: 4, border: "1px solid #d1d5db" }}>
                          <option value="">— grade —</option>
                          {specs.map(s => (
                            <option key={s.fgId} value={s.fgId}>{s.fgId}</option>
                          ))}
                        </select>
                      )}

                      {currentOutcome === "NEW_PRODUCT" && (
                        <input value={currentNewProd} placeholder="New product name…"
                          onChange={e => setSubEdits(p => ({ ...p, [sub.id]: { ...edit, newProductName: e.target.value } }))}
                          style={{ fontSize: 12, padding: "4px 8px", borderRadius: 4, border: "1px solid #d1d5db", width: 170 }} />
                      )}

                      <input value={currentNotes} placeholder="Notes…"
                        onChange={e => setSubEdits(p => ({ ...p, [sub.id]: { ...edit, outcomeNotes: e.target.value } }))}
                        style={{ fontSize: 12, padding: "4px 8px", borderRadius: 4, border: "1px solid #d1d5db", width: 150 }} />

                      <button className="btn btn-primary" style={{ fontSize: 12, padding: "4px 12px" }}
                        disabled={!isDirty || subSaving === sub.id}
                        onClick={() => handleUpdateSubmission(sub.id)}>
                        {subSaving === sub.id ? "Saving…" : "Save"}
                      </button>
                    </>
                  )}

                  <button className="btn" style={{ fontSize: 12, padding: "4px 12px" }}
                    onClick={() => setAnaModal(sub)}>
                    View Analysis
                  </button>
                </div>

                {!isDirty && (currentOutcome || currentNotes) && (
                  <div style={{ marginTop: 6, fontSize: 12, paddingLeft: 2 }}>
                    {currentOutcome === "GRADE_MATCH" && currentGrade && (
                      <span style={{ color: "#15803d", fontWeight: 600, marginRight: 8 }}>
                        Grade Match: {currentGrade}
                      </span>
                    )}
                    {currentOutcome === "NEW_PRODUCT" && currentNewProd && (
                      <span style={{ color: "#7c3aed", fontWeight: 600, marginRight: 8 }}>
                        New Product: {currentNewProd}
                      </span>
                    )}
                    {currentNotes && <span style={{ color: "#6b7280" }}>— {currentNotes}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Analysis Records ──────────────────────────────────────────────── */}
      <div style={{ margin: "24px 0 8px", fontWeight: 700, fontSize: 14, color: "#374151",
        borderTop: "1px solid #e5e7eb", paddingTop: 20 }}>
        Analysis Records
      </div>
      <div className="action-bar" style={{ marginTop: 0, marginBottom: 12 }}>
        <MonthYearSelect value={monthKey} onChange={setMonthKey} />
      </div>

      {!loading && !error && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filteredSamples.length === 0 && (
            <div style={{ textAlign: "center", color: "#aaa", padding: 32 }}>No analysis records this month.</div>
          )}
          {filteredSamples.map(sample => {
            const isOpen  = expanded === sample.id;
            const bestFit = !isOpen ? topMatch(sample) : null;
            return (
              <div key={sample.id} style={{
                border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", overflow: "hidden",
              }}>
                <div onClick={() => setExpanded(isOpen ? null : sample.id)}
                  style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 16px",
                    cursor: "pointer", userSelect: "none",
                    background: isOpen ? "#f0fdf4" : "#fff" }}>
                  <div style={{ minWidth: 90, fontSize: 12, color: "#6b7280" }}>{sample.date}</div>
                  <div style={{ flex: 1 }}>
                    <span className="fw-600">{sample.sampleRef}</span>
                  </div>
                  <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#374151" }}>
                    {sample.solidPct  != null && <span>Solids: <strong>{sample.solidPct}%</strong></span>}
                    {sample.viscosity != null && <span>Visc: <strong>{sample.viscosity} {sample.viscUnit === "B4_CUP" ? "s (B4)" : "cP"}</strong></span>}
                    {sample.ph        != null && <span>pH: <strong>{sample.ph}</strong></span>}
                  </div>
                  {!isOpen && bestFit && (
                    <span style={{ fontSize: 11, background: "#f0fdf4", color: "#15803d",
                      border: "1px solid #bbf7d0", borderRadius: 4, padding: "2px 8px", fontWeight: 600 }}>
                      → {bestFit}
                    </span>
                  )}
                  {!isOpen && !bestFit && sample.solidPct && (
                    <span style={{ fontSize: 11, background: "#fff7ed", color: "#c2410c",
                      border: "1px solid #fed7aa", borderRadius: 4, padding: "2px 8px" }}>
                      No match
                    </span>
                  )}
                  <span style={{ color: "#94a3b8", fontSize: 13 }}>{isOpen ? "▲" : "▼"}</span>
                  {canDelete && (
                    <button className="btn-small btn-danger"
                      onClick={e => { e.stopPropagation(); handleDelete(sample.id); }}>
                      Del
                    </button>
                  )}
                </div>
                {isOpen && (
                  <div style={{ padding: "12px 16px", borderTop: "1px solid #e2e8f0", background: "#fafafa" }}>
                    {sample.remarks && (
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                        Remarks: {sample.remarks}
                      </div>
                    )}
                    <RecommendationPanel sample={sample} specs={specs} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {anaModal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setAnaModal(null); }}>
          <AnalysisModal
            sub={anaModal}
            samples={samples}
            specs={specs}
            canEdit={canEdit}
            canDelete={canDelete}
            onLogSave={handleCreate}
            onDelete={handleDelete}
            onClose={() => setAnaModal(null)}
          />
        </div>
      )}
    </>
  );
}
